/**
 * Exhaustive status → UI mapping helpers (APP-002 §14, §36).
 *
 * Backend statuses (account status, subscription status, access state,
 * entitlement status) are rendered through exhaustive mappings so a new
 * backend value surfaces at compile time where practical, and degrades to a
 * neutral "Unknown" state at runtime where the value is a free-form string.
 *
 * Unknown values NEVER:
 *   - grant access,
 *   - promise plan features,
 *   - crash the dashboard.
 *
 * They render a neutral state instead (APP-002 §36).
 */
import type { SubscriptionStatus, AccessState } from '@/api/dashboard-types';
import type { BadgeVariant } from '@/components/ui/badge';

export interface StatusPresentation {
  /** Short label for badges / inline tags. */
  label: string;
  /** Badge variant; defaults to a neutral outline. */
  variant: BadgeVariant;
}

/* --------------------------------------------------------------------------
 * Account status (free-form string in the contract — DashboardAccount.status)
 *
 * The contract types `account_status` as a free-form string. The dashboard
 * renders it with a safe title-case presentation; only well-known values get
 * a semantic color. Unknown values degrade to a neutral badge and never imply
 * the account is healthy or disabled.
 * ------------------------------------------------------------------------ */

const KNOWN_ACCOUNT_STATUS: Record<string, StatusPresentation> = {
  active: { label: 'Active', variant: 'success' },
  disabled: { label: 'Disabled', variant: 'destructive' },
  suspended: { label: 'Suspended', variant: 'warning' },
  pending: { label: 'Pending', variant: 'info' },
};

export function presentAccountStatus(status: string | null | undefined): StatusPresentation {
  if (!status) return { label: 'Unknown', variant: 'outline' };
  const known = KNOWN_ACCOUNT_STATUS[status.toLowerCase()];
  if (known) return known;
  return { label: titleCase(status), variant: 'outline' };
}

/* --------------------------------------------------------------------------
 * Subscription status — the FINANCIAL Stripe state (SubscriptionStatus enum).
 * Distinct from runtime access (see presentAccessState). APP-002 §14.
 * ------------------------------------------------------------------------ */

export function presentSubscriptionStatus(
  status: SubscriptionStatus | string | null | undefined,
): StatusPresentation {
  switch (status) {
    case 'active':
      return { label: 'Active', variant: 'success' };
    case 'trialing':
      return { label: 'Trial', variant: 'info' };
    case 'past_due':
      return { label: 'Past due', variant: 'warning' };
    case 'unpaid':
      return { label: 'Unpaid', variant: 'destructive' };
    case 'canceled':
      return { label: 'Canceled', variant: 'outline' };
    case 'paused':
      return { label: 'Paused', variant: 'outline' };
    case 'incomplete':
      return { label: 'Incomplete', variant: 'warning' };
    case 'incomplete_expired':
      return { label: 'Incomplete', variant: 'outline' };
    default:
      return { label: 'Unknown', variant: 'outline' };
  }
}

/* --------------------------------------------------------------------------
 * Access state — the RUNTIME entitlement state (AccessState enum). This is the
 * authoritative signal for whether the account currently has paid access.
 * Never collapse this into subscription status (APP-002 §14).
 * ------------------------------------------------------------------------ */

export function presentAccessState(
  state: AccessState | string | null | undefined,
): StatusPresentation {
  switch (state) {
    case 'active':
      return { label: 'Active', variant: 'success' };
    case 'grace':
      return { label: 'Grace period', variant: 'warning' };
    case 'lapsed':
      return { label: 'Lapsed', variant: 'destructive' };
    case 'none':
      return { label: 'No access', variant: 'outline' };
    default:
      return { label: 'Unknown', variant: 'outline' };
  }
}

/* --------------------------------------------------------------------------
 * Entitlement status — free-form string from the overview `plan` object
 * (OverviewResponse.plan.entitlement_status). Lower-cased and matched against
 * known values; unknown values degrade to neutral.
 * ------------------------------------------------------------------------ */

export function presentEntitlementStatus(
  status: string | null | undefined,
): StatusPresentation {
  if (!status) return { label: 'Unknown', variant: 'outline' };
  switch (status.toLowerCase()) {
    case 'active':
    case 'subscription_active':
      return { label: 'Active', variant: 'success' };
    case 'grace':
    case 'grace_period':
      return { label: 'Grace period', variant: 'warning' };
    case 'lapsed':
    case 'expired':
      return { label: 'Lapsed', variant: 'destructive' };
    case 'none':
    case 'no_access':
    case 'inactive':
      return { label: 'Inactive', variant: 'outline' };
    case 'local':
      return { label: 'Local', variant: 'info' };
    default:
      return { label: titleCase(status.replace(/_/g, ' ')), variant: 'outline' };
  }
}

/**
 * Title-case a token like "past_due" → "Past Due". Used for free-form status
 * strings that have no dedicated mapping.
 */
export function titleCase(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}
